import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { PipelineStep } from '@/api/alibaba';

interface PipelineSummaryProps {
  steps: PipelineStep[];
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

interface SeriesStats {
  series: string;
  total: number;
  completed: number;
  failed: number;
  totalMs: number;
}

export function PipelineSummary({ steps }: PipelineSummaryProps) {
  const seriesOrder = ['A', 'B', 'C'];
  const statsMap = new Map<string, SeriesStats>();

  for (const s of seriesOrder) {
    statsMap.set(s, { series: s, total: 0, completed: 0, failed: 0, totalMs: 0 });
  }

  for (const step of steps) {
    const stat = statsMap.get(step.series);
    if (!stat) continue;
    stat.total++;
    if (step.status === 'completed') stat.completed++;
    if (step.status === 'failed') stat.failed++;
    if (step.duration_ms) stat.totalMs += step.duration_ms;
  }

  const totalCompleted = steps.filter(s => s.status === 'completed').length;
  const totalSteps = steps.length;
  const pct = totalSteps > 0 ? Math.round((totalCompleted / totalSteps) * 100) : 0;

  const SERIES_COLORS: Record<string, string> = { A: 'text-blue-600', B: 'text-orange-600', C: 'text-purple-600' };

  return (
    <div className="grid gap-3 md:grid-cols-4">
      {/* Overall */}
      <Card>
        <CardContent className="pt-4 pb-3 px-4 space-y-2">
          <div className="text-xs text-muted-foreground">整體進度</div>
          <div className="text-2xl font-bold">{totalCompleted}/{totalSteps}</div>
          <Progress value={pct} className="h-2" />
          <div className="text-xs text-muted-foreground">{pct}% 完成</div>
        </CardContent>
      </Card>

      {/* Per-series */}
      {seriesOrder.map(s => {
        const stat = statsMap.get(s)!;
        const seriesPct = stat.total > 0 ? Math.round((stat.completed / stat.total) * 100) : 0;
        return (
          <Card key={s}>
            <CardContent className="pt-4 pb-3 px-4 space-y-1">
              <div className={`text-xs font-semibold ${SERIES_COLORS[s]}`}>{s} 系列</div>
              <div className="text-lg font-bold">{stat.completed}/{stat.total}</div>
              <div className="text-xs text-muted-foreground">
                耗時: {stat.totalMs > 0 ? formatDuration(stat.totalMs) : '—'}
              </div>
              {stat.failed > 0 && (
                <div className="text-xs text-red-600">{stat.failed} 失敗</div>
              )}
              <Progress value={seriesPct} className="h-1.5" />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
